import { useMemo, useRef } from 'react';

import * as T from '../../../../components/Typography';
import { BasicInput } from '../../../../components/Inputs';
import * as S from './style';
import { Icon } from '../../../../components';
import { string, number, array, object } from 'yup';
import { useStepForm } from 'CMS/Providers/StepFormProvider';
import { MediaKeyInput } from 'CMS/components/MediaKeyInput';
import { mediaTypes } from 'constants';
import { useSiteSettings } from 'context/site-settings';
import { courseCodeTypes } from 'constants';

const initialData = {
  options: [{ id: 0, option: '' }],
  audioUrl: '',
  correctOptions: [{ id: 0, pre: '', answers: [''] }],
};

const WordJoin2 = () => {
  // Use useRef to keep the initial state constant across renders
  const initialRef = useRef(initialData);
  const { state, updateDataFields } = useStepForm(initialRef.current);
  const { data, validationErrs } = state;
  const { courseCode } = useSiteSettings();

  const options = useMemo(
    () =>
      data?.options?.map((e, i) => ({ ...e, id: i })) || [
        { id: 0, option: '' },
      ],
    [data?.options]
  );

  const correctOptions = useMemo(
    () =>
      data?.correctOptions?.map((e, i) => ({
        ...e,
        id: i,
      })) || [{ id: 0, pre: '', answers: [''] }],
    [data?.correctOptions]
  );

  const addOption = () => {
    const maxId = Math.max(...options.map((e) => e.id), 0);
    updateDataFields({
      options: [...options, { id: maxId + 1, option: '' }],
    });
  };
  const removeOption = (id) => {
    updateDataFields({
      options: options.filter((e) => e.id !== id),
    });
  };

  const addCorrectOptions = () => {
    const maxId = Math.max(...correctOptions.map((e) => e.id), 0);
    updateDataFields({
      correctOptions: [
        ...correctOptions,
        { id: maxId + 1, option: '', answers: [''] },
      ],
    });
  };
  const removeCorrectOptions = (id) => {
    updateDataFields({
      correctOptions: correctOptions.filter((e) => e.id !== id),
    });
  };
  const onOptionChange = (value, id) => {
    updateDataFields({
      options: options.map((e) => {
        if (e.id !== id) return e;
        return { ...e, option: value };
      }),
    });
  };

  const onCorrectOptionsPreChange = (value, id) => {
    updateDataFields({
      correctOptions: correctOptions.map((e) => {
        if (e.id !== id) return e;
        return { ...e, pre: value };
      }),
    });
  };

  const onCorrectOptionsAnswerChange = (value, ansI, id) => {
    updateDataFields({
      correctOptions: correctOptions.map((e) => {
        if (e.id !== id) return e;
        return {
          ...e,
          answers: e.answers.map((e, i) => (ansI === i ? value : e)),
        };
      }),
    });
  };
  const addCorrectAnswer = (id) => {
    updateDataFields({
      correctOptions: correctOptions.map((e) => {
        if (e.id !== id) return e;
        return {
          ...e,
          answers: [...e.answers, ''],
        };
      }),
    });
  };
  const removeLastAnswer = (id) => {
    updateDataFields({
      correctOptions: correctOptions.map((e) => {
        if (e.id !== id) return e;
        return {
          ...e,
          answers: e.answers.slice(0, -1),
        };
      }),
    });
  };

  return (
    <div>
      <BasicInput
        value={data.textAudio}
        handleChange={(textAudio) => updateDataFields({ textAudio })}
        label="Explainer Text"
        error={validationErrs.textAudio}
        m={{ mt: 8 }}
      />

      <MediaKeyInput
        label="Explainer Audio"
        type={mediaTypes.TEXT_AUDIO}
        value={data.textAudioKey}
        handleChange={(textAudioKey) => updateDataFields({ textAudioKey })}
        m={{ mt: 5 }}
        error={validationErrs.textAudioKey}
        requireVo
      />

      <T.P mt={8} size="large" weight="bold">
        Correct combinations
      </T.P>
      {validationErrs?.correctOptions &&
        typeof validationErrs.correctOptions === 'string' && (
          <T.P mb={2} mt={2} color="error">
            {validationErrs.correctOptions}
          </T.P>
        )}
      {correctOptions.map((e, i) => {
        return (
          <>
            <BasicInput
              label={`Pre text ${i + 1}`}
              value={e.pre}
              handleChange={(value) => {
                onCorrectOptionsPreChange(value, e.id);
              }}
              m={{ mb: 2, mt: 5 }}
              helper={`the prefix  e.g. ${
                courseCode === courseCodeTypes.CMID ? ' "5cm ="' : ' "un +"'
              }`}
              error={
                validationErrs?.correctOptions &&
                validationErrs?.correctOptions[i]?.pre
              }
            />

            <div
              style={{
                width: '95%',
                marginLeft: 'auto',
              }}
            >
              <T.P size="med" mt="6" weight="bold">
                Possible Answers
              </T.P>
              {e?.answers?.map((ans, ansI) => (
                <>
                  <BasicInput
                    value={ans}
                    handleChange={(value) => {
                      onCorrectOptionsAnswerChange(value, ansI, e.id);
                    }}
                    m={{ mb: 2, mt: 3 }}
                    error={validationErrs?.correctOptions?.[i]?.answers?.[ansI]}
                  />
                  {ansI === e.answers.length - 1 && ansI !== 0 && (
                    <S.Button
                      type="link"
                      danger
                      ghost
                      mt={1}
                      onClick={() => removeLastAnswer(e.id)}
                      ml="auto"
                    >
                      <Icon icon="cross" /> delete answer
                    </S.Button>
                  )}
                </>
              ))}
            </div>

            <S.Button
              type="link"
              ghost
              mt={5}
              mb={5}
              onClick={() => addCorrectAnswer(e.id)}
              disabled={e?.answers?.filter((_an) => !_an).length}
              ml="auto"
              mr="auto"
            >
              <Icon icon="plus" /> Add Answer
            </S.Button>

            {correctOptions.length === 1 && i === 0 ? null : (
              <S.Button
                type="link"
                danger
                ghost
                mt={1}
                onClick={() => removeCorrectOptions(e.id)}
                ml="auto"
              >
                <Icon icon="cross" />
              </S.Button>
            )}
          </>
        );
      })}

      <S.Button
        type="link"
        ghost
        mt={5}
        mb={5}
        onClick={addCorrectOptions}
        disabled={correctOptions.find((e) => !e.pre || !e?.answers?.length)}
        ml="auto"
        mr="auto"
      >
        <Icon icon="plus" /> Add a new row
      </S.Button>

      <T.P mt={8} size="large" weight="bold">
        Options
      </T.P>
      {validationErrs?.options &&
        typeof validationErrs.options === 'string' && (
          <T.P mb={2} mt={2} color="error">
            {validationErrs.options}
          </T.P>
        )}
      {options.map((e, i) => {
        const isDuplicate = options.some(
          (option) =>
            option.id !== e.id && option.option === e.option && e.option !== ''
        );
        return (
          <>
            <BasicInput
              label="Text"
              value={e.option}
              handleChange={(value) => {
                onOptionChange(value, e.id);
              }}
              m={{ mt: 5 }}
              helper={`single word e.g. ${
                courseCode === courseCodeTypes.CMID ? '"500mm"' : '"stand"'
              }`}
              error={
                (isDuplicate && 'This option already exists') ||
                (validationErrs?.options && validationErrs?.options[i]?.option)
              }
            />

            {options.length === 1 && i === 0 ? null : (
              <S.Button
                type="link"
                danger
                ghost
                mt={1}
                onClick={() => removeOption(e.id)}
                ml="auto"
              >
                <Icon icon="cross" />
              </S.Button>
            )}
          </>
        );
      })}
      <S.Button
        type="link"
        ghost
        mt={5}
        mb={5}
        onClick={addOption}
        disabled={options.find((e) => !e.option)}
        ml="auto"
        mr="auto"
      >
        <Icon icon="plus" /> Add Option
      </S.Button>
    </div>
  );
};

const validationSchema = {
  textAudio: string().notRequired(),
  textAudioKey: string().notRequired(),
  options: array()
    .of(
      object()
        .shape({
          id: number().notRequired(),
          option: string().required('required field'),
        })
        .required()
    )
    .required('You must provide options')
    .min(1, 'You must provide options')
    .test('uniqueValues', 'Options must be unique', function (values) {
      const options = values || [];
      const nonEmptyOptions = options.filter((o) => o.option);
      const uniqueOptions = new Set(nonEmptyOptions.map((o) => o.option));
      return uniqueOptions.size === nonEmptyOptions.length;
    }),
  correctOptions: array()
    .of(
      object()
        .shape({
          id: number().required(),
          pre: string().required('required field'),
          answers: array()
            .of(string().required('required field'))
            .required('Should add answers'),
        })
        .required()
    )
    .required('You must provide correct words')
    .min(1, 'You must provide correct words'),
};
export { validationSchema };

export default WordJoin2;
